<!-- eslint-disable vue/no-v-html -->
<template>
  <!-- credential result item container -->
  <div class="mb-8 content__sections">
    <div class="flex flex-col sm:flex-row gap-y-4 gap-x-8">
      <a
        :href="result.html_url"
        class="mt-1.5 block h-48 w-48 aspect-square"
      >
        <FocalImage
          v-if="result.thumbnail"
          :image="result.thumbnail as ImageWithFocalPoint"
          img-class="w-full h-full"
        />
      </a>

      <div class="flex flex-col gap-2">
        <h4>
          <a
            :href="result.html_url"
            v-html="highlightedTitle"
          />
        </h4>
        <div
          v-html="metaText"
          class="[&_p]:mb-0 caption-1"
        ></div>
        <p
          class="line-clamp-5 text-ellipsis"
        >{{ result.blurb }}</p>
        <div
          v-html="credential_icon"
          class="-mb-1 mt-1"
        ></div>
        <a :href="result.html_url" class="link-cta -left-1">LEARN MORE</a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { ISearchResult } from './CredentialList.vue'
import type { ImageWithFocalPoint } from '@/ts/types/focal-point'

/**
 * Component props
 */
const props = defineProps<{
  result: ISearchResult  // credential search result data
  query: string         // Search query for highlighting
}>()

/**
 * Computed property that highlights search terms in the credential title
 * Wraps matching query terms in <b> tags for visual emphasis
 */
const highlightedTitle = computed(() => {
  const title = props.result.title || ''
  if (!props.query) return title
  const regex = new RegExp(`(${decodeURI(props.query)})`, 'gi')
  return title.replace(regex, '<b>$1</b>')
})

const credential_topic = computed(() => {
  if (!props.result.get_topic_names || props.result.get_topic_names.length === 0) {
    return null
  }
  return props.result.get_topic_names.join('<span class="px-1">\u00a0\u2022\u00a0 </span>')
})

const credential_type = computed(() => {
  return props.result.get_credential_type || 'Credential'
})

const credential_icon = computed(() => {
  const typeStr = String(credential_type.value)
  if (typeStr.toLowerCase() === 'certificate') {
    return '<span class="credential-type credential-certificate">' +
              '<svg viewBox="0 0 30 30" class="icon-credential-certificate" title="icon-credential-certificate" aria-labelledby="title">' +
                '<use href="#icon-credential-certificate"></use>' +
              '</svg>' +
            '</span>'
  } else if (typeStr.toLowerCase() === 'authorization') {
    return '<span class="credential-type credential-authorization">' +
              '<svg viewBox="0 0 30 30" class="icon-credential-certificate" title="icon-credential-authorization" aria-labelledby="title">' +
                '<use href="#icon-credential-authorization"></use>' +
              '</svg>' +
            '</span>'
  } else {
    return ''
  }
})

/**
 * Combines publication date and type into a formatted metadata string
 * Uses bullet separator between date and type when both are available
 */
const metaText = computed(() => {
  const typeStr = String(credential_type.value)
  const topicStr = credential_topic.value ? String(credential_topic.value) : null

  if (typeStr && topicStr) {
    return `<p class="caption-1 mb-1"><span class="typeFilter">${typeStr.toUpperCase()}</span><span class="px-1">\u00a0\u2022\u00a0</span><span class="technicalAreaFilter">${topicStr.toUpperCase()}</span></p>`
  } else if (typeStr) {
    return typeStr.toUpperCase()
  } else if (topicStr) {
    return topicStr.toUpperCase()
  }
  return ''
})
</script>
